// Contract reader, text-layer version (no AI, no paid service). Input is the text pdf.js pulls out of a
// fillable/typed contract PDF; scanned contracts have no text layer and return nothing (needs OCR later).
// Supported: Florida Realtors/Florida Bar (FR/BAR) Residential Contract (incl. AS IS) and the NABOR
// residential contract. Every value is a suggestion: the form highlights what it filled for the agent to check.
// Day counts become dates with deadlines.js (Effective Date = day 0; FR/BAR rolls to the next business day).
const { deadlineFrom } = require('./deadlines');

const ws = (s) => String(s || '').replace(/[ \t\u00a0]+/g, ' ');
const money = (s) => { const n = parseFloat(String(s || '').replace(/[$,\s]/g, '')); return Number.isFinite(n) && n > 0 ? n : null; };
const MONEY = '\\$\\s*([\\d,]+(?:\\.\\d{2})?)';

function detectForm(text) {
  const t = ws(text);
  if (/Naples Area Board of REALTORS|\bNABOR\b/i.test(t)) return 'NABOR';
  if (/AS IS Residential Contract For Sale And Purchase/i.test(t)) return 'FRBAR_ASIS';
  if (/Residential Contract For Sale And Purchase/i.test(t) && /Florida (Realtors|Bar)/i.test(t)) return 'FRBAR';
  if (/Residential Contract For Sale And Purchase/i.test(t)) return 'FRBAR';
  return null;
}

// First capture of the first matching pattern.
const pick = (t, res) => { for (const re of res) { const m = t.match(re); if (m && m[1] && m[1].trim()) return m[1].trim(); } return ''; };

function parseDate(s) {
  const m = String(s || '').match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (m) { const y = m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3]); return `${y}-${String(m[1]).padStart(2, '0')}-${String(m[2]).padStart(2, '0')}`; }
  const d = new Date(String(s || '').replace(/(\d)(st|nd|rd|th)/, '$1'));
  return Number.isNaN(d.getTime()) || !/\d{4}/.test(s) ? '' : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// "within ___ (if left blank, then 15) days": the filled number, else the form's default.
function daysNear(t, anchor, fallback) {
  const i = t.search(anchor); if (i < 0) return null;
  const win = t.slice(i, i + 400);
  const m = win.match(/(\d{1,3}|_+)?\s*\(if left blank,? then\s+(\d{1,3})\)\s*days/i);
  if (m) { const filled = m[1] && /\d/.test(m[1]); return { days: Number(filled ? m[1] : m[2]), defaulted: !filled }; }
  const w = win.match(/within\s+(\d{1,3})\s*(?:calendar\s+|business\s+)?days/i);
  if (w) return { days: Number(w[1]), defaulted: false };
  return fallback ? { days: fallback, defaulted: true } : null;
}

function parseContract(rawText) {
  const t = ws(rawText);
  const form = detectForm(t);
  const fields = {}; const notes = [];
  if (!t.trim()) return { form: null, fields, notes: ['No text found: this PDF is probably a scan. Enter the fields by hand.'] };
  if (!form) notes.push('Not recognized as an FR/BAR or NABOR residential contract; matched what I could.');

  const sellerName = pick(t, [/PARTIES:\s*(.+?)\s*\(\s*"?Seller"?\s*\)/i, /Seller\(s\):\s*(.+?)(?:\s{2,}|\n|Buyer)/i]);
  const buyerName = pick(t, [/\(\s*"?Seller"?\s*\),?\s*and\s*(.+?)\s*\(\s*"?Buyer"?\s*\)/i, /Buyer\(s\):\s*(.+?)(?:\s{2,}|\n|Seller)/i]);
  const address = pick(t, [/Street address,? city,? zip:?\s*(.+?)(?:\n|\(b\)|Property Tax ID)/i, /Property Address:?\s*(.+?)(?:\n|Legal)/i]);
  if (sellerName) fields.sellerName = sellerName;
  if (buyerName) fields.buyerName = buyerName;
  if (address) fields.propertyAddress = address.replace(/\s*,\s*$/, '');

  const price = money(pick(t, [new RegExp(`PURCHASE PRICE[^$]{0,80}${MONEY}`, 'i'), new RegExp(`Purchase Price[^$]{0,40}${MONEY}`, 'i')]));
  if (price) fields.offerPrice = price;
  const dep1 = money(pick(t, [new RegExp(`Initial deposit[^$]{0,200}${MONEY}`, 'i')]));
  if (dep1) { fields.firstEscrowAmount = dep1; const d = daysNear(t, /Initial deposit/i, 3); if (d) { fields.firstEscrowDueDateDays = d.days; if (d.defaulted) notes.push(`Initial deposit: form default of ${d.days} days used.`); } }
  const dep2 = money(pick(t, [new RegExp(`Additional deposit[^$]{0,300}${MONEY}`, 'i')]));
  if (dep2) { fields.secondEscrowAmount = dep2; const d = daysNear(t, /Additional deposit/i, 10); if (d) { fields.secondEscrowDueDateDays = d.days; if (d.defaulted) notes.push(`Additional deposit: form default of ${d.days} days used.`); } }
  const loan = money(pick(t, [new RegExp(`Financing[^$]{0,200}${MONEY}`, 'i')]));
  if (loan) fields.loanAmount = loan;

  const closing = pick(t, [/CLOSING DATE:[\s\S]{0,200}?(\d{1,2}\/\d{1,2}\/\d{2,4}|[A-Z][a-z]+\.?\s+\d{1,2}(?:st|nd|rd|th)?,?\s+\d{4})/i, /Closing Date:?\s*(\d{1,2}\/\d{1,2}\/\d{2,4})/i]);
  if (closing && parseDate(closing)) fields.closingDate = parseDate(closing);

  const insp = daysNear(t, /Inspection Period/i, form && form.startsWith('FRBAR') ? 15 : null);
  if (insp) { fields.homeInspectionDeadlineDays = insp.days; if (insp.defaulted) notes.push(`Inspection Period: form default of ${insp.days} days used.`); }
  const fin = daysNear(t, /Loan Approval Period/i, null) || daysNear(t, /Financing Contingency/i, null);
  if (fin && loan) { fields.financingDeadlineDays = fin.days; if (fin.defaulted) notes.push(`Loan Approval Period: form default of ${fin.days} days used.`); }
  const appr = daysNear(t, /Appraisal Contingency/i, null);
  if (appr) fields.appraisalDeadlineDays = appr.days;

  const eff = pick(t, [/Effective Date:?\s*(\d{1,2}\/\d{1,2}\/\d{2,4})/i]);
  if (eff) fields.contractDate = parseDate(eff);
  else notes.push('Effective Date not filled on the contract (it is the date of the last signature/initial): enter it so the deadlines can be counted.');
  if (form === 'FRBAR' || form === 'FRBAR_ASIS') notes.push('FR/BAR: deadlines that land on a weekend or holiday roll to the next business day (calendar-day counting).');
  return { form, fields, notes };
}

// Deadlines from the parsed day counts (needs an effective date: parsed or entered).
function deadlinesFor(fields, effectiveDate) {
  const out = {};
  Object.keys(fields).filter((k) => k.endsWith('Days')).forEach((k) => {
    const r = deadlineFrom(effectiveDate, fields[k], 'calendar');
    if (r) out[k.slice(0, -4)] = r.date;
  });
  return out;
}

module.exports = { detectForm, parseContract, deadlinesFor, parseDate };
